import { createRouter, createWebHistory } from 'vue-router'
import ROUTES from './routes.js'
import { useRoles } from '@/composables/useRoles.js'
import ROLES from '@roles'
import { useCorporationScope } from '@/composables/useCorporationScope.js'
import { toValue } from 'vue'
import CORPORATION_SCOPE_TYPE from '@/constants/corporationScopeType.js'

const routes = [
    {
        path: '/',
        name: ROUTES.HOME,
        component: () => import('@views/TicketHolderPage.vue'),
        meta: {
            rights: [ ROLES.ACCESS_TICKET_HOLDERS ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS, CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES ],
            nav: true,
            display: 'Schüler'
        }
    },
    {
        path: '/accounting',
        name: ROUTES.ACCOUNTING,
        component: () => import('@views/AccountingPage.vue'),
        meta: {
            rights: [ ROLES.ADMINISTRATOR, ROLES.ACCESS_ACCOUNTING ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES ],
            nav: true,
            display: 'Abrechnung'
        }
    },
    {
        path: '/school-assignment-change',
        name: ROUTES.SCHOOL_ASSIGNMENT_CHANGE,
        component: () => import('@views/SchoolAssignmentChangePage.vue'),
        meta: {
            rights: [ ROLES.ACCESS_SCHOOL_ASSIGNMENT_CHANGES ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS ],
            nav: true,
            display: 'Versetzung',
            parentPath: '/',
        }
    },
    {
        path: '/school-and-classes',
        name: ROUTES.SCHOOL_AND_CLASSES,
        component: () => import('@views/SchoolPage.vue'),
        meta: {
            rights: [ ROLES.ADMINISTRATOR, ROLES.ACCESS_CLASSES, ROLES.ACCESS_SCHOOL ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS ],
            nav: true,
            display: 'Schule und Klassen'
        }
    },
    {
        path: '/users',
        name: ROUTES.USERS,
        component: () => import('@views/UserManagementPage.vue'),
        meta: {
            rights: [ ROLES.USER_MANAGEMENT ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS, CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES ],
            nav: true,
            display: 'Benutzer'
        }
    },
    {
        path: '/applications',
        name: ROUTES.APPLICATIONS,
        component: () => import('@views/application-requests/ApplicationsPage.vue'),
        meta: {
            rights: [ ROLES.ACCESS_APPLICATIONS ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS ],
            nav: true,
            display: 'Anträge'
        }
    },
    {
        path: '/ticket-holder',
        name: ROUTES.TICKETHOLDER,
        component: () => import('@views/TicketHolderDetailPage.vue'),
        meta: {
            rights: [ ROLES.ACCESS_TICKET_HOLDERS ],
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS, CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES ],
            nav: false,
            display: 'Schüler',
            parentPath: '/',
            basePath: '/'
        }
    },
    {
        path: '/products',
        name: ROUTES.PRODUCT,
        component: () => import('@views/ProductSelectionPage.vue'),
        meta: {
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS ],
            nav: false,
            display: 'Produktauswahl',
            parentPath: '/ticket-holder',
            basePath: '/'
        }
    },
    {
        path: '/ticket-creation/:productVariantId',
        name: ROUTES.TICKETCREATION,
        component: () => import('@views/TicketCreationPage.vue'),
        meta: {
            allowedCorporationScopes: [ CORPORATION_SCOPE_TYPE.SCHOOLS ],
            nav: false,
            display: 'Berechtigung anlegen',
            parentPath: '/products',
            basePath: '/'
        }
    },
    // catch all non existing routes
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        // always redirect to landing page
        beforeEnter: (to, from, next) => {
            next({ path: '/', query: to.query })
        }
    }
]
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
})

//somehow on first load some components can not be loaded dynamically
//this function catches this error and reloads page until better solution is found
//https://stackoverflow.com/questions/69300341/typeerror-failed-to-fetch-dynamically-imported-module-on-vue-vite-vanilla-set
router.onError(async (error, to) => {
    const dynamicallyImportedModuleError = error.message.includes('Failed to fetch dynamically imported module')
    if (dynamicallyImportedModuleError)
    {
        if (!to?.fullPath) {
            window.location.reload()
        } else {
            window.location = to.fullPath
        }
    }
})

router.beforeEach((to/*, from*/) => {
    const { hasRole } = useRoles()
    const { corporationScope } = useCorporationScope()
    // check if the user is allowed to navigate to the given page
    const rightsToRoute = to.meta.rights
    if (rightsToRoute && !rightsToRoute.find(right => hasRole(right))) {
        return _getAllowedDefaultRoute()
    }
    const corporationScopes = to.meta.allowedCorporationScopes || []
    if ( corporationScopes.length && !corporationScopes?.includes(toValue(corporationScope)) ) {
        return _getAllowedDefaultRoute()
    }
})

const _getAllowedDefaultRoute = () => {
    const { hasRole } = useRoles()
    // if not, send the user to the the first page he is allowed to access
    for (const route of routes) {
        const routeHasRights = !!route.meta?.rights
        const routeIsNavigatable = !!route.meta?.nav && !route.meta?.explicitNoNav
        const userIsAllowedToUseRoute = !!route.meta?.rights?.find(right => hasRole(right))

        // 1. the route needs to be accesible by navigation
        // 2. either the route needs no rights or the user is allowed to use it
        if (routeIsNavigatable && (!routeHasRights || userIsAllowedToUseRoute)) {
            return route
        } else {
            console.warn('user is not allowed to access route')
        }
    }

    // if even that fails, try to redirect to the landing page
    // -> possible endless loop if the user does not have the right to go to the landing page
    return { name: ROUTES.HOME }
}

export default router

export {
    ROUTES
}
